// Frontend-only connectivity detector. Watches `navigator.onLine` plus a
// rolling count of consecutive API failures driven by the axios response
// interceptor. Surfaces "offline" state to subscribers (the
// AnnouncementsContext) so a synthetic banner can render without doing
// any DB round-trip — important because the DB might be exactly what's
// broken.
//
// Thresholds locked in the plan doc:
//   - N = 3 consecutive 5xx / timeout failures → raise
//   - Single 2xx response → recover immediately
//   - navigator.onLine = false → raise immediately (no hysteresis; the
//     browser is authoritative)

const FAIL_THRESHOLD = 3;

const state = {
  consecutiveFailures: 0,
  browserOffline: typeof navigator !== 'undefined' && navigator.onLine === false
};

const listeners = new Set();

export function getState() {
  return {
    isOffline:
      state.browserOffline || state.consecutiveFailures >= FAIL_THRESHOLD,
    consecutiveFailures: state.consecutiveFailures,
    browserOffline: state.browserOffline
  };
}

function emit() {
  const snapshot = getState();
  for (const fn of listeners) {
    try {
      fn(snapshot);
    } catch (_e) {
      // Listener errors shouldn't break the detector.
    }
  }
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function onApiSuccess() {
  if (state.consecutiveFailures === 0) return;
  state.consecutiveFailures = 0;
  emit();
}

export function onApiFailure() {
  state.consecutiveFailures += 1;
  // Only re-emit at the threshold boundary or every step so subscribers
  // can update — but we keep a tight emit policy to avoid spam.
  if (
    state.consecutiveFailures === FAIL_THRESHOLD ||
    state.consecutiveFailures > FAIL_THRESHOLD
  ) {
    emit();
  }
}

// Browser online/offline events. navigator.onLine flips before the
// events fire, so we mirror it into our state too.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    state.browserOffline = false;
    state.consecutiveFailures = 0;
    emit();
  });
  window.addEventListener('offline', () => {
    state.browserOffline = true;
    emit();
  });
}
